import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Sparkles, ShoppingBag, BarChart3, Mail } from 'lucide-react';

interface LandingProps {
  onNavigate?: (page: string) => void;
}

export const Landing: React.FC<LandingProps> = () => {

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="text-center">
            <img
              src="/brand/logo-horizontal-darkbg.svg"
              alt="SmartCatalog"
              className="h-16 sm:h-20 mx-auto mb-10"
            />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              AI-powered product cataloging
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed">
              Upload product photos. Get instant AI analysis — titles, descriptions,
              attributes, and image classification. Sync directly to Square.
            </p>
            <SignupForm />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 text-center mb-4">
            How it works
          </h2>
          <p className="text-lg text-slate-500 text-center mb-16 max-w-2xl mx-auto">
            Three steps from photos to published product listing.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <StepCard
              step={1}
              icon={Camera}
              title="Upload photos"
              description="Drag and drop product images or import directly from Google Drive. Upload front, back, and detail shots."
            />
            <StepCard
              step={2}
              icon={Sparkles}
              title="AI analyzes"
              description="AI classifies each image, identifies the product, generates titles, descriptions, and attributes — all in one pass."
            />
            <StepCard
              step={3}
              icon={ShoppingBag}
              title="Publish to Square"
              description="Review the AI suggestions, adjust if needed, and sync directly to your Square catalog with one click."
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 sm:py-28 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 text-center mb-16">
            Built for product sellers
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureCard
              icon={Camera}
              title="Smart image ordering"
              description="AI classifies front, back, and detail images and orders them automatically."
            />
            <FeatureCard
              icon={Sparkles}
              title="AI product descriptions"
              description="Generate customer-ready titles and market descriptions from product photos."
            />
            <FeatureCard
              icon={ShoppingBag}
              title="Square integration"
              description="Products, categories, and images sync directly to your Square catalog."
            />
            <FeatureCard
              icon={BarChart3}
              title="Custom attributes"
              description="Define your own classification schema — the AI adapts to your categories."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img
            src="/brand/icon-mark.svg"
            alt=""
            className="h-16 mx-auto mb-8"
          />
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            Ready to streamline your catalog?
          </h2>
          <p className="text-lg text-slate-500 mb-10">
            Stop writing product descriptions by hand. Let AI do the heavy lifting.
          </p>
          <SignupForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/brand/icon-mark-darkbg.svg" alt="" className="h-8" />
              <span className="text-slate-300 font-medium">SmartCatalog</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            </div>
            <p className="text-sm">
              &copy; {new Date().getFullYear()} Winding River Software
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

function StepCard({ step, icon: Icon, title, description }: {
  step: number;
  icon: React.FC<{ className?: string }>;
  title: string;
  description: string;
}) {
  return (
    <div className="text-center">
      <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-600 mb-6">
        <Icon className="w-7 h-7 text-white" />
        <span className="absolute -top-1 -right-1 w-7 h-7 bg-slate-900 text-white text-xs font-bold rounded-full flex items-center justify-center">
          {step}
        </span>
      </div>
      <h3 className="text-xl font-semibold text-slate-900 mb-3">{title}</h3>
      <p className="text-slate-500 leading-relaxed">{description}</p>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description }: {
  icon: React.FC<{ className?: string }>;
  title: string;
  description: string;
}) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-blue-50 mb-4">
        <Icon className="w-6 h-6 text-blue-600" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>
      <p className="text-slate-500 text-sm leading-relaxed">{description}</p>
    </div>
  );
}

function SignupForm() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
    } catch {
      // Silently handle — we'll show success regardless so the UX is clean
      // The backend endpoint may not exist yet
    }
    setSubmitted(true);
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-6 max-w-md mx-auto">
        <p className="text-green-800 font-medium">Thanks! We'll be in touch.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-md mx-auto">
      <p className="text-sm text-slate-500 mb-4">Sign up to get early access</p>
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="email"
            required
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-900"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors whitespace-nowrap"
        >
          {submitting ? 'Sending...' : 'Get Access'}
        </button>
      </div>
    </form>
  );
}
